import { createContext, useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { useTranslation } from "react-i18next";
import useAuth from "../hooks/useAuth";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import Config from "../config";
import playNotificationSound, { unlockAudioContext } from "../pages/Chat/utils/notificationSound";
import showMessageNotification from "../pages/Chat/utils/browserNotification";

const ChatContext = createContext({});

export const ChatProvider = ({ children }) => {
    const { t } = useTranslation();
    const { auth, setToast } = useAuth();
    const axiosPrivate = useAxiosPrivate();
    const axiosRef = useRef(axiosPrivate);
    axiosRef.current = axiosPrivate;
    const myUserId = auth?.user?.id;

    const [socket, setSocket] = useState(null);
    const [presence, setPresence] = useState({});
    const [unreadTotal, setUnreadTotal] = useState(0);
    // Which conversations are muted, so the 'message:new' handler below can skip sound/browser
    // notification for them — a ref (not state) since it's only read inside that socket callback,
    // and putting it in the effect's dependency array would reconnect the socket on every mute toggle.
    const mutedGroupIdsRef = useRef(new Set());

    const refreshUnreadTotal = useCallback(async () => {
        try {
            const res = await axiosRef.current.get('/chat-messages/unread-total');
            setUnreadTotal(res.data?.data?.total || 0);
        } catch (error) { /* silent — badge just stays as-is */ }
    }, []);

    const refreshMutedIds = useCallback(async () => {
        try {
            const res = await axiosRef.current.get('/chat-groups/muted-ids');
            mutedGroupIdsRef.current = new Set(res.data?.data || []);
        } catch (error) { /* silent — worst case a muted chat still notifies until next refresh */ }
    }, []);

    // Lets the Chat page update this tab's notification gating the instant it mutes/unmutes a
    // conversation, without waiting on a refetch — other tabs pick it up on their next refresh.
    const setGroupMuted = useCallback((chatGroupId, muted) => {
        if (muted) mutedGroupIdsRef.current.add(chatGroupId);
        else mutedGroupIdsRef.current.delete(chatGroupId);
    }, []);

    // Finishes a join that JoinGroup.jsx couldn't complete because the visitor wasn't logged
    // in yet — it stashed the invite code in localStorage before the app's own auth guard took
    // over and sent them to sign-in. Runs once per login here (this provider mounts app-wide,
    // so it fires however the user ends up back in the app, not just if they land on /chat).
    const completePendingInvite = useCallback(async () => {
        const code = localStorage.getItem('pendingChatInviteCode');
        if (!code) return;
        localStorage.removeItem('pendingChatInviteCode');

        try {
            const res = await axiosRef.current.post(`/chat-groups/invite/${code}/join`);
            setToast({ content: t('JOINED_GROUP_VIA_LINK', { name: res.data?.data?.name }), variant: 'success', title: t('SUCCESS'), show: true });
        } catch (error) { /* silent — link may have been revoked/invalid by the time login finished */ }
    }, [setToast, t]);

    useEffect(() => {
        const events = ['pointerdown', 'keydown', 'touchstart'];
        const unlock = () => {
            unlockAudioContext();
            events.forEach(event => document.removeEventListener(event, unlock));
        };
        events.forEach(event => document.addEventListener(event, unlock, { passive: true }));
        return () => events.forEach(event => document.removeEventListener(event, unlock));
    }, []);

    useEffect(() => {
        if (!auth?.token || auth.token === 'Unauthorized') {
            setSocket(null);
            return;
        }

        const instance = io(Config.URL_SERVER, {
            path: '/api/socket.io',
            auth: { token: auth.token },
            withCredentials: true,
        });

        instance.on('presence', ({ userId, status, lastActivity }) => {
            setPresence(prev => ({ ...prev, [userId]: { status, lastActivity } }));
        });

        instance.on('message:new', ({ data }) => {
            refreshUnreadTotal();
            // muting only silences the sound/OS notification — unreadTotal above still counts it
            if (data?.senderId && data.senderId !== myUserId && !mutedGroupIdsRef.current.has(data.chatGroupId)) {
                playNotificationSound();
                showMessageNotification(data.sender?.fullName, data.body);
            }
        });
        instance.on('message:read', refreshUnreadTotal);
        instance.on('chat-group:new', refreshUnreadTotal);

        setSocket(instance);
        refreshUnreadTotal();
        refreshMutedIds();
        completePendingInvite();

        return () => { instance.disconnect(); };
    }, [auth?.token, myUserId, refreshUnreadTotal, refreshMutedIds, completePendingInvite]);

    return (
        <ChatContext.Provider value={{ socket, presence, unreadTotal, refreshUnreadTotal, setUnreadTotal, setGroupMuted }}>
            {children}
        </ChatContext.Provider>
    );
}

export default ChatContext;
